import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ShieldCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { IMAGES } from "@/lib/images";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function AdminLogin() {
    const { login, user } = useAuth();
    const navigate = useNavigate();
    const [form, setForm] = useState({ email: "", password: "" });
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (user) navigate("/admin", { replace: true });
    }, [user, navigate]);

    if (user) return null;

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            await login(form.email, form.password);
            toast.success("Welcome back, Commissioner.");
            navigate("/admin");
        } catch (err) {
            setError(apiError(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div data-testid="admin-login-page" className="relative min-h-screen flex items-center justify-center overflow-hidden">
            <img src={IMAGES.hoopRim} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
            <div className="absolute inset-0 bg-gradient-to-b from-[#08080C]/80 via-[#08080C]/60 to-[#08080C]" />
            <div className="spotlight spotlight-ember top-[-15%] right-[-10%]" />

            <div className="relative z-10 w-full max-w-md mx-4">
                <form
                    onSubmit={submit}
                    data-testid="admin-login-form"
                    className="bg-[#12121A]/90 backdrop-blur-xl border border-[#262638] rounded-3xl p-8 sm:p-10 shadow-2xl"
                >
                    <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-xl bg-[#FF5500] flex items-center justify-center shadow-[0_0_24px_rgba(255,85,0,0.5)]">
                            <ShieldCheck className="w-5 h-5 text-white" />
                        </div>
                        <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-[#FFB800]">
                            League Office
                        </p>
                    </div>
                    <h1 className="font-display font-black uppercase text-3xl text-white">Admin Access</h1>
                    <p className="text-sm text-slate-400 mt-2 mb-8">
                        Manage teams, rosters, schedules and results.
                    </p>

                    {error && (
                        <div
                            data-testid="admin-login-error"
                            className="mb-6 bg-[#EF4444]/10 border border-[#EF4444]/40 text-[#EF4444] text-sm rounded-xl px-4 py-3"
                        >
                            {error}
                        </div>
                    )}

                    <div className="space-y-5">
                        <div>
                            <Label htmlFor="admin-email" className="text-xs uppercase tracking-[0.2em] text-slate-400">
                                Email or Username
                            </Label>
                            <Input
                                id="admin-email"
                                data-testid="admin-login-email-input"
                                type="text"
                                autoComplete="username"
                                required
                                value={form.email}
                                onChange={(e) => setForm({ ...form, email: e.target.value })}
                                className="mt-2 bg-[#08080C] border-[#262638] focus:border-[#FF5500] text-white"
                                placeholder="you@league.com or username"
                            />
                        </div>
                        <div>
                            <Label htmlFor="admin-password" className="text-xs uppercase tracking-[0.2em] text-slate-400">
                                Password
                            </Label>
                            <Input
                                id="admin-password"
                                data-testid="admin-login-password-input"
                                type="password"
                                required
                                value={form.password}
                                onChange={(e) => setForm({ ...form, password: e.target.value })}
                                className="mt-2 bg-[#08080C] border-[#262638] focus:border-[#FF5500] text-white"
                                placeholder="••••••••"
                            />
                        </div>
                    </div>

                    <button
                        type="submit"
                        data-testid="admin-login-submit-button"
                        disabled={loading}
                        className="mt-8 w-full flex items-center justify-center gap-2 bg-[#FF5500] hover:bg-[#E04800] disabled:opacity-60 text-white font-display font-bold uppercase tracking-widest py-3.5 rounded-full transition-colors duration-200"
                    >
                        {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                        Enter the console
                    </button>
                </form>
                <p className="text-center mt-6">
                    <Link to="/" data-testid="admin-login-back-home" className="text-xs text-slate-500 hover:text-[#FF5500] transition-colors duration-200">
                        ← Back to the arena
                    </Link>
                </p>
            </div>
        </div>
    );
}
